import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 124 Backoffice SOS Cases API
// (BackofficeSosController/SosCaseService). Same base/auth convention as
// ../abnormal/abnormalApi.js/../faceCheck/faceCheckApi.js - apiClient.get/post
// with no `base` override, the same Bearer-session-authenticated backend
// every other backoffice controller uses. This file never calls a real
// SMS/LINE/push notification service and never calls an external
// emergency-service integration - every write here maps 1:1 to one of the
// three real P124 routes below.

const SOS_PATH = "/backoffice/sos/cases";

// Exactly the query param names BackofficeSosController.ListCases declares,
// in the same order as the controller signature - anything else passed to
// listSosCases is silently dropped rather than forwarded, so a typo'd key
// can never accidentally reach the backend as an unfiltered pass-through.
// Scope-isolated visibility always happens on the backend from the caller's
// own session, never from a value this file sends.
const CASES_LIST_PARAM_KEYS = [
  "search",
  "caseStatus",
  "sosType",
  "severity",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "workerId",
  "page",
  "pageSize",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listSosCases(params = {}) {
  return apiClient.get(`${SOS_PATH}${buildQuery(CASES_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty supportCaseId - this file never
// issues a request for a missing/blank id (the caller only calls this once
// a row/card is actually clicked).
export function getSosCaseDetail(supportCaseId) {
  if (!supportCaseId) {
    throw new Error("getSosCaseDetail requires a supportCaseId");
  }
  return apiClient.get(`${SOS_PATH}/${encodeURIComponent(supportCaseId)}`);
}

export function acknowledgeSosCase(supportCaseId, { note } = {}) {
  if (!supportCaseId) {
    throw new Error("acknowledgeSosCase requires a supportCaseId");
  }
  return apiClient.post(`${SOS_PATH}/${encodeURIComponent(supportCaseId)}/acknowledge`, { note: note || null });
}

export function resolveSosCase(supportCaseId, { resolutionNote } = {}) {
  if (!supportCaseId) {
    throw new Error("resolveSosCase requires a supportCaseId");
  }
  return apiClient.post(`${SOS_PATH}/${encodeURIComponent(supportCaseId)}/resolve`, { resolutionNote: resolutionNote || null });
}
